import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillCaretDown } from 'react-icons/ai';
import { CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';
import { AuthState, useAuth } from '../../../features/auth/AuthProvider';
import {
  useGetUserCoinsMutation,
  useGetUserInfoQuery,
} from '../../../services/api/apiProvider';
import { ImageCustom } from '../../../common/components/ImageCustom';
import goLiveBtn from '../../../assets/svg/go_live_btn.svg';
import chargeDiamond from '../../../assets/svg/charge1.svg';
import diamondIcon from '../../../assets/png/diamond.png';
import './UserInfo.scss';
import { formatBigNumber } from '../../../common/utils/utils';
import { toast, ToastContainer } from 'react-toastify';
import { LiveMissionModal } from '../../../features/live/components/LiveMission/LiveMissionModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/store';
import {
  toggleModalMission,
  setCurrentUser,
} from '../../../features/profile/slices/profilePageSlice';
import { reproio } from '../../../common/utils/reproio';
import { APP_BASE_URL, CHARGE_URL } from '../../../loadEnv';

export const UserInfo = (): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [openMenu, setOpenMenu] = useState(false);
  const [openFeatureMenu, setOpenFeatureMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const featureMenuRef = useRef<HTMLDivElement>(null);
  const [getUserCoin] = useGetUserCoinsMutation();

  // user data from query api
  const { data: user, isLoading } = useGetUserInfoQuery();
  useEffect(() => {
    if (user) {
      dispatch(setCurrentUser(user));
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      reproio(
        'setStringUserProfile',
        'Certification',
        user?.liverLeague === 1 ? '有' : '無',
      );
    }
  }, [user, user?.liverLeague]);

  // log out hook
  const { logOut, userCoin, setUserCoin } = useAuth() as AuthState;

  const isShowModalMission = useSelector(
    (state: AppState) => state.profilePageState.isShowModalMission,
  );

  const toggleMenu = (isOpen: boolean) => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick, false);
    } else {
      document.removeEventListener('click', handleOutsideClick, false);
    }

    setOpenMenu(isOpen);
    setOpenFeatureMenu(false);
  };

  // Add Voice feature
  const toggleFeatureMenu = (isOpen: boolean) => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick, false);
    } else {
      document.removeEventListener('click', handleOutsideClick, false);
    }
    setOpenFeatureMenu(isOpen);
    setOpenMenu(false);
  };

  const handleOutsideClick = (e: any) => {
    if (!ref.current?.contains(e.target)) {
      toggleMenu(false);
      toggleFeatureMenu(false);
    }
  };

  const handleLogOut = (): void => {
    logOut();
  };

  const handleGoLive = (): void => {
    reproio('track', 'tap broadcast button');
    setOpenMenu(false);
    // show error if liverLeague !== 1, mean not verified liver
    if (user?.liverLeague !== 1) {
      toast.error(t('RequireVerified'));
      return;
    }

    // voice feature
    const isRedirectVoice = window.location.href.indexOf('/voice') > -1;
    if (isRedirectVoice) {
      window.location.href = `${window.location.origin}/${APP_BASE_URL}live`;
      return;
    }

    const livePathIndex = window.location.href.indexOf('/live');
    if (
      livePathIndex > 0 &&
      livePathIndex !== window.location.href.length - 5
    ) {
      // when go '/live' from audience, try to reload page to clear data
      window.location.href = `${window.location.origin}/${APP_BASE_URL}live`;
    } else {
      history.push('/live');
    }
  };

  const handleGoVoice = (): void => {
    reproio('track', 'tap broadcast button');
    setOpenFeatureMenu(false);
    // show error if liverLeague !== 1, mean not verified liver
    if (user?.liverLeague !== 1) {
      toast.error(t('RequireVerified'));
      return;
    }

    // voice feature
    const isRedirectLive = window.location.href.indexOf('/live');
    if (isRedirectLive > 0) {
      window.location.href = `${window.location.origin}/${APP_BASE_URL}voice`;
      return;
    }

    const voicePathIndex = window.location.href.indexOf('/voice');
    if (
      voicePathIndex > 0 &&
      voicePathIndex !== window.location.href.length - 5
    ) {
      // when go '/live' from audience, try to reload page to clear data
      window.location.href = `${window.location.origin}/${APP_BASE_URL}voice`;
    } else {
      history.push('/voice');
    }
  };

  const handleGoProfile = () => {
    history.push('/profile');
    toggleMenu(false);
  };

  const handleGoWallet = () => {
    history.push('/wallet');
    toggleMenu(false);
  };
  const callBackMissionModal = (child: boolean) => {
    dispatch(toggleModalMission(child));
  };

  const handleGoMission = () => {
    dispatch(toggleModalMission(true));
    toggleMenu(false);
  };

  const handleGoBuyDiamond = () => {
    reproio('track', 'tap gift button');
    window.open(CHARGE_URL);
  };

  const renderMenu = (): ReactElement => {
    return (
      <CSSTransition
        nodeRef={menuRef}
        in={openMenu}
        timeout={300}
        unmountOnExit
        classNames="app-bar__user__menu"
      >
        <div ref={menuRef} className="app-bar__user__menu">
          <ul>
            <li className="app-bar__user__menu-item" onClick={handleGoProfile}>
              {t('Profile')}
            </li>
            <li className="app-bar__user__menu-item" onClick={handleGoWallet}>
              ウォレット
            </li>
            <li className="app-bar__user__menu-item" onClick={handleGoMission}>
              ミッション
            </li>
            <li
              className="app-bar__user__menu-item--logout"
              onClick={handleLogOut}
            >
              {t('Logout')}
            </li>
          </ul>
        </div>
      </CSSTransition>
    );
  };

  const renderFeatureMenu = (): ReactElement => {
    return (
      <CSSTransition
        classNames="app-bar__feature__menu"
        unmountOnExit
        timeout={300}
        nodeRef={featureMenuRef}
        in={openFeatureMenu}
      >
        <div ref={featureMenuRef} className="app-bar__feature__menu">
          <ul>
            <li
              className="app-bar__feature__menu-item"
              onClick={() => handleGoLive()}
            >
              Live
            </li>
            <li
              className="app-bar__feature__menu-item"
              onClick={() => handleGoVoice()}
            >
              Voice
            </li>
          </ul>
        </div>
      </CSSTransition>
    );
  };

  useEffect(() => {
    getUserCoin()
      .unwrap()
      .then((response) => {
        setUserCoin(response.coin);
      });
  }, []);

  if (isLoading) {
    return <></>;
  }

  return (
    <div ref={ref} className="app-bar__user">
      <div className="app-bar__user__diamond-count">
        <ImageCustom
          className="app-bar__user__diamond-icon"
          src={diamondIcon}
        />
        <span className="app-bar__user__diamond-number">
          {formatBigNumber(userCoin)}
        </span>
      </div>

      <ImageCustom
        className="app-bar__user__charge"
        src={chargeDiamond}
        onClick={handleGoBuyDiamond}
      />
      <ImageCustom
        className="app-bar__user__go-live"
        src={goLiveBtn}
        onClick={() => {
          toggleFeatureMenu(!openFeatureMenu);
        }}
      />
      {renderFeatureMenu()}
      <div
        className="app-bar__user__button"
        onClick={() => {
          toggleMenu(!openMenu);
        }}
      >
        <ImageCustom
          className="app-bar__user__profile-icon"
          src={user?.picture.uri}
        />
        <div className="app-bar__user__name">{user?.loginId}</div>
        <AiFillCaretDown />
      </div>
      {renderMenu()}
      <LiveMissionModal
        callback={callBackMissionModal}
        isOpenModalMission={isShowModalMission}
      />

      <ToastContainer
        position="top-center"
        autoClose={3000}
        limit={1}
        pauseOnHover={false}
      ></ToastContainer>
    </div>
  );
};
